// @ts-ignore
import {HttpClient, HttpParams} from '@angular/common/http';
// @ts-ignore
import {Injectable} from '@angular/core';
// @ts-ignore
import {Observable} from 'rxjs';

export interface AerosysEmpresa {
    id: number;
    nome?: string;
    razao?: string;
    cnpj?: string;
}

export interface AerosysOrdemServico {
    id: number;
    numOs?: string;
    status?: string;
    totalGeral?: number;
    dataEmissao?: string;
    clienteNome?: string;
    aeronaveMatricula?: string;
    tipo?: string;
}

export interface AerosysItemOs {
    id: number;
    quant?: number;
    partNumber?: string;
    [key: string]: unknown;
}

export interface AerosysOrdemServicoDetalhe {
    id: number;
    numOs?: string;
    status?: string;
    listaItens?: AerosysItemOs[];
    [key: string]: unknown;
}

/** Venda (dashboard leve) — usada na listagem do "Sistema Legado" (Aerosys). */
export interface AerosysVenda {
    id: number;
    numTitulo?: string;
    status?: string;
    totFaturado?: number;
    totalDev?: number;
    totalMoeda?: number;
    totalLiquido?: number;
    dataEmissao?: string;
    clienteNome?: string;
    tipo?: string;
}

export interface AerosysItemVenda {
    id: number;
    quant?: number;
    /** Part Number/descrição real vêm do adendo de peça vinculado ao item — ITENS_VENDA
     *  não tem uma coluna própria de Part Number como ITENS_OS (IOS_PART_NUMBER) tem. */
    adendoPecas?: { pn?: string; descricao?: string; [key: string]: unknown };
    descricao?: string;
    /** Desconto/acréscimo do item no Aerosys — convenção INVERTIDA da nossa:
     *  negativo = desconto, positivo = acréscimo (ver inversão de sinal ao
     *  mapear em gateway.component.ts#onSelecionarVendaLegado). */
    descAcres?: number;
    [key: string]: unknown;
}

export interface AerosysVendaDetalhe {
    id: number;
    numTitulo?: string;
    status?: string;
    listaItens?: AerosysItemVenda[];
    [key: string]: unknown;
}

/**
 * Venda "rica" para o Painel de Separação — mesmos campos que o painel de
 * Separação do próprio Aerosys já mostra nativamente (vendedor, separador,
 * início da separação, prioridade, faturamento imediato, progresso de itens).
 * Vem do endpoint /venda/{idEmpresa}/separacao-painel (ver VendaSeparacaoDTO
 * no projeto Aerosys) — usada para fundir essas vendas na MESMA tabela do
 * Painel de Separação do GPL-Integracao, em vez de uma seção à parte.
 */
export interface AerosysVendaSeparacao {
    id: number;
    numTitulo?: string;
    dataEmissao?: string;
    status?: string;
    prioridade?: string;
    fatImediato?: string;
    dataAuto?: string;
    clienteNome?: string;
    vendedorNome?: string;
    separadorNome?: string | null;
    totalItens?: number;
    itensSeparados?: number;
    /** "Sininho" -- itens com pendência "Item no outro Galpão" já RESOLVIDA (alguém já buscou)
     *  mas ainda não marcados como separados de verdade -- ver resolverPendencia no Aerosys. */
    itensPendenciaResolvida?: number;
    dataInicioSep?: string | null;
    /** AERONAVE.AER_MATRICULA (via VENDA.AER_ID) -- pode ser nulo. */
    prefixoAeronave?: string | null;
}

/**
 * Venda "rica" para o Painel de Conferência Legado — mesmo espírito de
 * AerosysVendaSeparacao, mas pro Painel de Conferência: vem do endpoint
 * /venda/{idEmpresa}/conferencia-painel (ver VendaConferenciaLegadoDTO no
 * pacote com.infoaries.aerosys.gplconferencia do projeto Aerosys). Sem
 * dataInicioConf -- a Conferência Legado não expõe início de sessão no
 * painel (só o conferente atual, quando há sessão ativa).
 */
export interface AerosysVendaConferencia {
    id: number;
    numTitulo?: string;
    dataEmissao?: string;
    status?: string;
    prioridade?: string;
    fatImediato?: string;
    clienteNome?: string;
    vendedorNome?: string;
    /** Quem está conferindo agora (sessão ativa) -- pode ser nulo (ainda não iniciada). */
    conferenteNome?: string | null;
    totalItens?: number;
    itensConferidos?: number;
    /** "Sininho" -- itens com pendência "Item no outro Galpão" já RESOLVIDA mas ainda não confirmados (IVD_SAIDA continua 'N'). */
    itensPendenciaResolvida?: number;
    /** AERONAVE.AER_MATRICULA (via VENDA.AER_ID) -- pode ser nulo (a consulta da Conferência ainda não faz esse join). */
    prefixoAeronave?: string | null;
}

/**
 * Item da venda legado na tela de "Conferência" (cega). Vem dos endpoints
 * /venda/conferencia-legado/{idVenda}/iniciar e /itens do Aerosys (ver
 * ItemConferenciaLegadoDTO no pacote com.infoaries.aerosys.gplconferencia).
 * `quantidade` é sempre a quantidade AINDA PENDENTE (sobra, após um
 * eventual desmembramento) -- não a quantidade originalmente vendida.
 * `saida` espelha ITENS_VENDA.IVD_SAIDA diretamente ('S' = já conferido).
 */
export interface AerosysItemConferenciaLegado {
    ivdId: number;
    apeId?: number;
    pn?: string;
    descricao?: string;
    quantidade?: number;
    estoque?: number | null;
    local?: string | null;
    cd?: string | null;
    controlaSerial?: boolean;
    saida: 'S' | 'N';
    codusu?: number | null;
    conferenteNome?: string | null;
    dataConferencia?: string | null;
    serialEscolhido?: string | null;
    formEscolhido?: string | null;
    consumoLotes?: AerosysConsumoLote[] | null;
    observacao?: string | null;
    /** "Item no outro Galpão" -- true quando existe pendência ABERTA pra este item (ver ConferenciaPendenciaLegado no Aerosys). */
    pendente?: boolean | null;
    pendenteObservacao?: string | null;
}

/**
 * Prévia (sem gravar nada) de como um item vai ficar se o conferente
 * confirmar uma quantidade MENOR que a pendente (vai desmembrar). Vem do
 * endpoint /venda/conferencia-legado/{idVenda}/item/{idItemVenda}/preview-
 * desmembramento do Aerosys (ver PreviewDesmembramentoDTO no pacote
 * com.infoaries.aerosys.gplconferencia). Usada pra mostrar um modal de
 * confirmação ("confirma o desmembramento?") ANTES de chamar
 * confirmarContagemConferenciaLegado de verdade.
 */
export interface AerosysPreviewDesmembramento {
    ivdId: number;
    pn?: string | null;
    descricao?: string | null;
    quantidadeOriginal?: number | null;
    quantidadeConferida?: number | null;
    /** Como fica a linha ATUAL (sobra, ainda pendente) -- 0 quando NÃO desmembra (bateu certinho). */
    quantidadeRestante?: number | null;
    subTotalAtual?: number | null;
    subTotalMoedaAtual?: number | null;
    /** Como fica a linha NOVA (parte atendida agora). */
    subTotalNovo?: number | null;
    subTotalMoedaNovo?: number | null;
}

/**
 * Item PENDENTE ("Item no outro Galpão") da CONFERÊNCIA na fila global
 * "Itens Pendentes". Vem do endpoint /venda/conferencia-legado/pendencias
 * do Aerosys (ver ItemPendenteConferenciaLegadoDTO no pacote
 * com.infoaries.aerosys.gplconferencia).
 */
export interface AerosysItemPendenteConferenciaLegado {
    gplCodpend: number;
    venId: number;
    ivdId: number;
    pn?: string | null;
    descricao?: string | null;
    quantidade?: number | null;
    estoque?: number | null;
    local?: string | null;
    cd?: string | null;
    controlaSerial?: boolean | null;
    codusu?: number | null;
    separadorNome?: string | null;
    dataSinalizacao?: string | null;
    observacao?: string | null;
}

/**
 * Um "pedaço" (chunk) do consumo de um item, quando a quantidade pedida na
 * venda não cabe inteira num único lote de compra -- ex.: item pede 10, o
 * lote mais antigo só tem 3 disponíveis, então vem em vários chunks: {form
 * A, quantidade 3}, {form B, quantidade 4}, {form C, quantidade 2}, {form D,
 * quantidade 1}. Ver ConsumoLoteDTO no pacote gplseparacao do projeto
 * Aerosys. `lote` (ICO_FORM_LOTE) quase sempre vem nulo -- só exibir quando
 * presente. `serial` só vem preenchido pra peças que controlam série.
 */
export interface AerosysConsumoLote {
    icoId: number;
    form?: string | null;
    lote?: string | null;
    quantidade?: number;
    serial?: string | null;
}

/**
 * Item da venda legado na tela de "Continuar Separação". Vem do endpoint
 * /venda/separacao-legado/{idVenda}/continuar do Aerosys (ver
 * ItemSeparacaoLegadoDTO no pacote isolado com.infoaries.aerosys.gplseparacao
 * do projeto Aerosys) — serial e FORM/lote já vêm escolhidos por FIFO quando
 * a peça controla série. Sem validação de posse: qualquer usuário pode
 * continuar/marcar itens de uma separação já iniciada por outra pessoa.
 */
export interface AerosysItemSeparacaoLegado {
    ivdId: number;
    apeId?: number;
    pn?: string;
    descricao?: string;
    quantidade?: number;
    /** Saldo de estoque e local de armazenagem (ADENDO_PECAS_ESTOQUE, por peça+empresa)
     *  — mesma informação que o relatório nativo do Aerosys já mostra. */
    estoque?: number | null;
    local?: string | null;
    /** Condição da peça (AdendoPecas.APE_CD) — ex.: "NEW". */
    cd?: string | null;
    controlaSerial?: boolean;
    separado: 'S' | 'N';
    codusu?: number;
    separadorNome?: string | null;
    serialEscolhido?: string | null;
    formEscolhido?: string | null;
    dataSeparacao?: string | null;
    /** Detalhamento completo de consumo (todos os chunks, mesmo quando só tem 1) -- ver AerosysConsumoLote. */
    consumoLotes?: AerosysConsumoLote[] | null;
    /** Observação livre POR ITEM (ex.: "não tem o item", "quantidade encontrada é menor"). */
    observacao?: string | null;
    /** "Item no outro Galpão" -- true quando existe pendência ABERTA pra este item (ver SeparacaoPendenciaLegado no Aerosys). */
    pendente?: boolean | null;
    /** Nota livre da pendência (ex.: "está no galpão 2, prateleira B") -- null quando pendente=false. */
    pendenteObservacao?: string | null;
}

/**
 * Item PENDENTE ("Item no outro Galpão") na fila global "Itens Pendentes" --
 * junta itens de VÁRIAS vendas diferentes. Vem do endpoint
 * /venda/separacao-legado/pendencias do Aerosys (ver ItemPendenteLegadoDTO no
 * pacote com.infoaries.aerosys.gplseparacao). Mesmos campos que
 * AerosysItemSeparacaoLegado (pn, descricao, quantidade, estoque, local, cd,
 * controlaSerial) + número da venda e quem sinalizou/quando.
 */
export interface AerosysItemPendenteLegado {
    gplCodpend: number;
    venId: number;
    ivdId: number;
    pn?: string | null;
    descricao?: string | null;
    quantidade?: number | null;
    estoque?: number | null;
    local?: string | null;
    cd?: string | null;
    controlaSerial?: boolean | null;
    codusu?: number | null;
    separadorNome?: string | null;
    dataSinalizacao?: string | null;
    observacao?: string | null;
}

/**
 * Um lote de compra candidato pra troca manual do form (e do serial, quando a
 * peça controla série) de um item da separação legado. Ver LoteDisponivelDTO
 * no pacote com.infoaries.aerosys.gplseparacao do projeto Aerosys.
 */
export interface AerosysLoteDisponivel {
    icoId: number;
    form?: string | null;
    /** ICO_FORM_LOTE -- quase sempre nulo, exibido só quando presente. */
    lote?: string | null;
    dataCompra?: string | null;
    /** Contagem de seriais disponíveis (peça com serial) ou saldo (peça sem serial). */
    qtdDisponivel?: number | null;
}

/** Um serial físico disponível dentro de um lote (2ª etapa da troca manual). */
export interface AerosysSerialDisponivel {
    icsId: number;
    ns?: string | null;
}

/**
 * Usuário do PRÓPRIO Aerosys (tabela USUARIO) — usado tanto na identificação
 * "quem sou você" ao abrir a tela (o Aerosys não tem como saber sozinho quem
 * está usando a tela, já que toda chamada do GPL-Integracao usa um usuário de
 * serviço fixo) quanto no picker de separadores adicionais do Dividir.
 */
export interface AerosysUsuarioSeparador {
    codusu: number;
    nome?: string | null;
}

/**
 * Chama os endpoints /api/aerosys/** do gateway (backend GPL-Integracao),
 * que por sua vez se autentica sozinho na API do Aerosys (sem digitação humana).
 */
// @ts-ignore
@Injectable({providedIn: 'root'})
export class AerosysGatewayService {
    private readonly apiUrl = '/api/aerosys';

    constructor(private readonly http: HttpClient) {
    }

    listarEmpresas(): Observable<AerosysEmpresa[]> {
        return this.http.get<AerosysEmpresa[]>(`${this.apiUrl}/empresas`);
    }

    /**
     * @param idEmpresa id da empresa no Aerosys
     * @param startDate formato dd-MM-yyyy
     * @param endDate   formato dd-MM-yyyy
     */
    listarOrdensServico(idEmpresa: number, startDate: string, endDate: string): Observable<AerosysOrdemServico[]> {
        const params = new HttpParams()
            .set('startDate', startDate)
            .set('endDate', endDate)
            .set('dashboard', 'true');
        return this.http.get<AerosysOrdemServico[]>(`${this.apiUrl}/ordens-servico/${idEmpresa}`, {params});
    }

    /** OS completa (com listaItens[].partNumber) — usada para importar itens no Pedido de Venda. */
    buscarOrdemServico(idEmpresa: number, id: number): Observable<AerosysOrdemServicoDetalhe> {
        return this.http.get<AerosysOrdemServicoDetalhe>(`${this.apiUrl}/ordens-servico/${idEmpresa}/${id}`);
    }

    /**
     * @param idEmpresa id da empresa no Aerosys
     * @param startDate formato dd-MM-yyyy
     * @param endDate   formato dd-MM-yyyy
     */
    listarVendas(idEmpresa: number, startDate: string, endDate: string): Observable<AerosysVenda[]> {
        const params = new HttpParams()
            .set('startDate', startDate)
            .set('endDate', endDate);
        return this.http.get<AerosysVenda[]>(`${this.apiUrl}/vendas/${idEmpresa}`, {params});
    }

    /** Venda completa (com listaItens[].adendoPecas.pn) — usada para importar itens no Pedido de Venda. */
    buscarVenda(idEmpresa: number, id: number): Observable<AerosysVendaDetalhe> {
        return this.http.get<AerosysVendaDetalhe>(`${this.apiUrl}/vendas/${idEmpresa}/${id}`);
    }

    /**
     * Versão "rica" das Vendas Autorizado/Separação, com vendedor/separador/
     * prioridade/imediato/progresso — usada para fundir as vendas do Aerosys
     * na tabela do Painel de Separação.
     */
    listarSeparacaoPainel(idEmpresa: number): Observable<AerosysVendaSeparacao[]> {
        return this.http.get<AerosysVendaSeparacao[]>(`${this.apiUrl}/vendas/${idEmpresa}/separacao-painel`);
    }

    /**
     * Busca usuários do próprio Aerosys por nome — identificação "quem sou
     * você" ao abrir a tela e picker de separadores adicionais do Dividir.
     */
    buscarUsuariosSeparacaoLegado(termo: string): Observable<AerosysUsuarioSeparador[]> {
        const params = termo ? new HttpParams().set('termo', termo) : undefined;
        return this.http.get<AerosysUsuarioSeparador[]>(`${this.apiUrl}/vendas/separacao-legado/usuarios`, {params});
    }

    /**
     * Continua (ou inicia) a separação item-a-item de uma venda do Sistema
     * Legado, pro separador informado (codusu/tag) — garante a sessão de
     * separação e retorna os itens, com serial/FORM já escolhidos
     * automaticamente por FIFO.
     */
    continuarSeparacaoLegado(idVenda: number, codusu: number, tag?: string | null): Observable<AerosysItemSeparacaoLegado[]> {
        return this.http.post<AerosysItemSeparacaoLegado[]>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/continuar`,
            {codusu, tag: tag ?? null});
    }

    /** Marca/desmarca um item da separação como separado. */
    /** Retorna só o item alterado (não a lista inteira) -- ver comentário em SeparacaoLegadoService.marcarItem no Aerosys. */
    marcarItemSeparacaoLegado(idVenda: number, idItemVenda: number, separado: boolean, codusu: number): Observable<AerosysItemSeparacaoLegado> {
        const params = new HttpParams().set('codusu', codusu);
        return this.http.put<AerosysItemSeparacaoLegado>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/item/${idItemVenda}/${separado}`, {}, {params});
    }

    /** Finaliza a separação da venda (exige todos os itens já marcados). */
    finalizarSeparacaoLegado(idVenda: number): Observable<void> {
        return this.http.post<void>(`${this.apiUrl}/vendas/separacao-legado/${idVenda}/finalizar`, {});
    }

    /** Lotes de compra disponíveis pra trocar manualmente o form/serial de um item. */
    lotesDisponiveisSeparacaoLegado(idVenda: number, idItemVenda: number): Observable<AerosysLoteDisponivel[]> {
        return this.http.get<AerosysLoteDisponivel[]>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/item/${idItemVenda}/lotes-disponiveis`);
    }

    /** Seriais físicos disponíveis dentro de um lote (2ª etapa da troca manual, só pra peça com serial). */
    seriaisDoLoteSeparacaoLegado(idVenda: number, idItemVenda: number, idLote: number): Observable<AerosysSerialDisponivel[]> {
        return this.http.get<AerosysSerialDisponivel[]>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/item/${idItemVenda}/lote/${idLote}/seriais-disponiveis`);
    }

    /** Troca manualmente o form (e serial, se aplicável) escolhido pelo FIFO pra um item. */
    trocarFormSeparacaoLegado(idVenda: number, idItemVenda: number, icoId: number, icsId: number | null, codusu: number): Observable<AerosysItemSeparacaoLegado[]> {
        return this.http.put<AerosysItemSeparacaoLegado[]>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/item/${idItemVenda}/trocar-form`,
            {icoId, icsId: icsId ?? null, codusu});
    }

    /** Divide a separação entre o chamador + separadores adicionais (até 2, mesmo padrão do Sankhya). */
    dividirSeparacaoLegado(idVenda: number, codusuChamador: number, novosCodusu: number[]): Observable<AerosysItemSeparacaoLegado[]> {
        return this.http.post<AerosysItemSeparacaoLegado[]>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/dividir`,
            {codusuChamador, novosCodusu});
    }

    /** Transfere a separação inteira do separador origem pra outro (destino) — mesmo padrão do Sankhya. */
    transferirSeparacaoLegado(idVenda: number, codusuOrigem: number, codusuDestino: number): Observable<AerosysItemSeparacaoLegado[]> {
        return this.http.post<AerosysItemSeparacaoLegado[]>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/transferir`,
            {codusuOrigem, codusuDestino});
    }

    /** Observação livre POR ITEM (não geral da venda) -- ex.: "não tem o item", "quantidade encontrada é menor". */
    /** Retorna só o item alterado (não a lista inteira) -- ver comentário em SeparacaoLegadoService.salvarObservacaoItem no Aerosys. */
    salvarObservacaoItemSeparacaoLegado(idVenda: number, idItemVenda: number, observacao: string, codusu: number): Observable<AerosysItemSeparacaoLegado> {
        return this.http.put<AerosysItemSeparacaoLegado>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/item/${idItemVenda}/observacao`,
            {observacao, codusu});
    }

    /** Sinaliza um item como "Item no outro Galpão" -- não trava a separação, permite concluir mesmo com o item ainda não encontrado. */
    marcarItemPendenteSeparacaoLegado(idVenda: number, idItemVenda: number, codusu: number, observacao?: string | null): Observable<AerosysItemSeparacaoLegado> {
        return this.http.put<AerosysItemSeparacaoLegado>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/item/${idItemVenda}/pendencia`,
            {codusu, observacao: observacao ?? null});
    }

    /** Desfaz uma sinalização de pendência feita por engano (não resolve -- só remove o sinal). */
    desfazerPendenciaSeparacaoLegado(idVenda: number, idItemVenda: number): Observable<AerosysItemSeparacaoLegado> {
        return this.http.put<AerosysItemSeparacaoLegado>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/item/${idItemVenda}/pendencia/desfazer`, {});
    }

    /** Resolve a pendência (item foi buscado no outro galpão) -- usado pela tela "Itens Pendentes".
     *  Não exige identificação -- codusu é opcional (auditoria best-effort, não trava o fluxo). */
    resolverPendenciaSeparacaoLegado(idVenda: number, idItemVenda: number, codusu?: number | null): Observable<AerosysItemSeparacaoLegado> {
        return this.http.put<AerosysItemSeparacaoLegado>(
            `${this.apiUrl}/vendas/separacao-legado/${idVenda}/item/${idItemVenda}/pendencia/resolver`,
            {codusu: codusu ?? null});
    }

    /** Fila global (todas as vendas) de pendências abertas -- tela "Itens Pendentes". */
    listarPendenciasSeparacaoLegado(): Observable<AerosysItemPendenteLegado[]> {
        return this.http.get<AerosysItemPendenteLegado[]>(`${this.apiUrl}/vendas/separacao-legado/pendencias`);
    }

    // ========================================================================
    // Conferência do Sistema Legado (Aerosys)
    // ========================================================================

    /**
     * Versão "rica" das vendas em Conferência Legado (conferente, prioridade,
     * imediato, progresso de itens, sininho de pendência resolvida) --
     * usada para fundir essas vendas na MESMA tabela do Painel de Conferência
     * do GPL-Integracao.
     */
    listarConferenciaPainelLegado(idEmpresa: number): Observable<AerosysVendaConferencia[]> {
        return this.http.get<AerosysVendaConferencia[]>(`${this.apiUrl}/vendas/${idEmpresa}/conferencia-painel`);
    }

    /** Busca usuários do próprio Aerosys por nome -- identificação "quem sou você" ao abrir a tela de conferência. */
    buscarUsuariosConferenciaLegado(termo: string): Observable<AerosysUsuarioSeparador[]> {
        const params = termo ? new HttpParams().set('termo', termo) : undefined;
        return this.http.get<AerosysUsuarioSeparador[]>(`${this.apiUrl}/vendas/conferencia-legado/usuarios`, {params});
    }

    /** Abre (ou reaproveita) a sessão ativa de conferência de uma venda, pro conferente informado, e retorna os itens. */
    iniciarConferenciaLegado(idVenda: number, codusu: number): Observable<AerosysItemConferenciaLegado[]> {
        return this.http.post<AerosysItemConferenciaLegado[]>(
            `${this.apiUrl}/vendas/conferencia-legado/${idVenda}/iniciar`, {codusu});
    }

    /** Lista todos os itens da venda (pendentes e já confirmados) na conferência. */
    listarItensConferenciaLegado(idVenda: number): Observable<AerosysItemConferenciaLegado[]> {
        return this.http.get<AerosysItemConferenciaLegado[]>(`${this.apiUrl}/vendas/conferencia-legado/${idVenda}/itens`);
    }

    /**
     * Prévia (sem gravar nada) de como o item ficará se essa quantidade for
     * confirmada e for menor que a pendente (vai desmembrar) -- chamar ANTES
     * de confirmarContagemConferenciaLegado pra mostrar o modal "confirma o
     * desmembramento?" com o item atual e o item novo.
     */
    previewDesmembramentoConferenciaLegado(idVenda: number, idItemVenda: number, quantidade: number): Observable<AerosysPreviewDesmembramento> {
        const params = new HttpParams().set('quantidade', quantidade);
        return this.http.get<AerosysPreviewDesmembramento>(
            `${this.apiUrl}/vendas/conferencia-legado/${idVenda}/item/${idItemVenda}/preview-desmembramento`, {params});
    }

    /**
     * Confirma a contagem cega de um item -- aqui acontece a saída REAL de
     * estoque e, se a quantidade contada for menor que a pendente, o
     * desmembramento do item (ver ConferenciaLegadoService#confirmarContagem
     * no Aerosys). Retorna a lista inteira (não só o item) porque o
     * desmembramento pode mudar a quantidade de linhas.
     */
    confirmarContagemConferenciaLegado(idVenda: number, idItemVenda: number, quantidade: number, codusu: number): Observable<AerosysItemConferenciaLegado[]> {
        return this.http.put<AerosysItemConferenciaLegado[]>(
            `${this.apiUrl}/vendas/conferencia-legado/${idVenda}/item/${idItemVenda}/confirmar`,
            {quantidade, codusu});
    }

    /** Sinaliza um item como "Item no outro Galpão" na conferência -- não trava, permite concluir mesmo com o item ainda não encontrado. */
    marcarItemPendenteConferenciaLegado(idVenda: number, idItemVenda: number, codusu: number, observacao?: string | null): Observable<AerosysItemConferenciaLegado> {
        return this.http.put<AerosysItemConferenciaLegado>(
            `${this.apiUrl}/vendas/conferencia-legado/${idVenda}/item/${idItemVenda}/pendencia`,
            {codusu, observacao: observacao ?? null});
    }

    /** Desfaz uma sinalização de pendência de conferência feita por engano. */
    desfazerPendenciaConferenciaLegado(idVenda: number, idItemVenda: number): Observable<AerosysItemConferenciaLegado> {
        return this.http.put<AerosysItemConferenciaLegado>(
            `${this.apiUrl}/vendas/conferencia-legado/${idVenda}/item/${idItemVenda}/pendencia/desfazer`, {});
    }

    /** Resolve a pendência de conferência (item foi buscado no outro galpão) -- usado pela tela "Itens Pendentes". */
    resolverPendenciaConferenciaLegado(idVenda: number, idItemVenda: number, codusu?: number | null): Observable<AerosysItemConferenciaLegado> {
        return this.http.put<AerosysItemConferenciaLegado>(
            `${this.apiUrl}/vendas/conferencia-legado/${idVenda}/item/${idItemVenda}/pendencia/resolver`,
            {codusu: codusu ?? null});
    }

    /** Fila global (todas as vendas) de pendências de conferência abertas -- tela "Itens Pendentes". */
    listarPendenciasConferenciaLegado(): Observable<AerosysItemPendenteConferenciaLegado[]> {
        return this.http.get<AerosysItemPendenteConferenciaLegado[]>(`${this.apiUrl}/vendas/conferencia-legado/pendencias`);
    }

    /** Finaliza a conferência da venda -- recalcula VEN_STATUS pra PRONTO ou ESTOQUE. */
    finalizarConferenciaLegado(idVenda: number, codusu?: number | null): Observable<{ status: string }> {
        return this.http.post<{ status: string }>(
            `${this.apiUrl}/vendas/conferencia-legado/${idVenda}/finalizar`, {codusu: codusu ?? null});
    }
}
